// The auction's block (AUCTION.md §10): what is up for bids, whose nomination
// it is, my money, and the bids. It sits where a pick draft's clock bar does,
// so the thing the room is waiting on is always in view.
//
// THE ARITHMETIC IS THE ROOM'S. Every number here — the high bid, the most a
// team can bid, the spots left — comes in the view; this draws them and
// sends intents. A second client gets the same numbers from the same wire.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { DraftAuction, DraftAuctionLot, DraftView } from '@fsp/domain';
import type { DetailLevel } from '@fsp/theme';
import { PositionPill } from '../../kit';
import { Button, Callout, Caption, Dialog, Line, NumberField, Stack, Text } from '../../kit/ui';
import { PlayerName } from '../shared/Names';
import { Term } from '../../kit/Why';
import { clubLabel } from '../../sports/registry';

export interface AuctionActions {
  /** Put a player up at an opening bid: for my team, or, as commissioner, for the team nominating. */
  nominate: (playerId: string, bid: number, forTeam: string | null) => void;
  bid: (lotId: number, amount: number) => void;
  /** The most the room bids to for me; null takes it off. */
  autoBid: (lotId: number, max: number | null) => void;
}

const money = (n: number) => `$${String(n)}`;

/** A clock's time left: minutes and seconds, or hours in a slow auction. */
export function auctionClock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s >= 3600) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${String(h)}h ${String(m).padStart(2, '0')}m`;
  }
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, '0')}`;
}

/** Whether an auction's clocks run in hours (AUCTION.md §5): no going once there. */
export const isSlow = (a: DraftAuction) => a.rules.bidSeconds >= 3600;

/** My team's money, from the room's own numbers. */
export function myMoney(view: DraftView): DraftAuction['budgets'][number] | null {
  return view.auction?.budgets.find((b) => b.teamId === view.myTeamId) ?? null;
}

/** "You: $63 left · most $57 · 6 spots" — or the roster full. */
export function moneyLine(view: DraftView): string | null {
  const mine = myMoney(view);
  if (mine === null) return null;
  if (mine.spotsLeft === 0) return `You: ${money(mine.left)} left · roster full`;
  return `You: ${money(mine.left)} left · most ${money(mine.most)} · ${String(mine.spotsLeft)} ${mine.spotsLeft === 1 ? 'spot' : 'spots'}`;
}

/**
 * The words on a lot's clock: going once and twice in a live auction's last
 * seconds (AUCTION.md §3.5), the time left otherwise.
 */
function clockWords(lot: DraftAuctionLot, auction: DraftAuction, now: number, stopped: boolean) {
  if (lot.deadlineAt === null || stopped) return { text: '—', urgent: false };
  const left = Date.parse(lot.deadlineAt) - now;
  if (!isSlow(auction) && left <= 3_000) return { text: 'Going twice…', urgent: true };
  if (!isSlow(auction) && left <= 6_000) return { text: 'Going once…', urgent: true };
  return { text: auctionClock(left), urgent: !isSlow(auction) && left <= 10_000 };
}

/** One player up: his clock, the high bid, and my bids on him. */
function LotCard({
  view,
  lot,
  now,
  actions,
  usable,
  stopped,
  detail,
  nameOf,
  onAsk,
}: {
  view: DraftView;
  lot: DraftAuctionLot;
  now: number;
  actions: AuctionActions;
  usable: boolean;
  stopped: boolean;
  detail: DetailLevel;
  nameOf: (teamId: string) => string;
  onAsk: (ask: Ask) => void;
}) {
  const auction = view.auction!;
  const live = view.status === 'live';
  const ahead = lot.leaderTeamId === view.myTeamId;
  const seat = view.myTeamId !== null;
  const one = lot.price + 1;
  const five = lot.price + 5;
  const can = (amount: number) => usable && live && seat && !ahead && amount <= lot.myMost;
  const clock = clockWords(lot, auction, now, stopped || view.status === 'paused');
  const where = [
    lot.player.positions[0],
    lot.player.proTeam === null ? 'FA' : clubLabel(lot.player.proTeam),
    detail !== 'simple' && lot.value !== null ? `value ${money(lot.value)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const standing = ahead
    ? `You're ahead at ${money(lot.price)}`
    : `High bid ${money(lot.price)} · ${nameOf(lot.leaderTeamId)}`;
  // WHY I CANNOT BID, said where the buttons would be.
  const why = !seat
    ? null
    : ahead
      ? null
      : lot.myMost === 0
        ? 'Your roster is full, counting the players you are ahead on.'
        : lot.myMost < one
          ? `The most you can bid is ${money(lot.myMost)}.`
          : null;
  return (
    <div className="fsp-dr-lot" data-ahead={ahead || undefined}>
      <div className="fsp-dr-lotwho">
        <PositionPill pos={lot.player.positions[0] ?? '—'} size="sm" />
        <div className="fsp-ui-grow fsp-dr-who">
          <div className="fsp-name">
            <PlayerName player={lot.player} className="fsp-name-text" />
          </div>
          <div className="fsp-sub">{where}</div>
        </div>
      </div>
      <span
        className="fsp-num fsp-disp fsp-dr-lotclock"
        data-urgent={clock.urgent || undefined}
        aria-live="off"
      >
        {clock.text}
      </span>
      <div className="fsp-dr-lotprice">
        <span className="fsp-num fsp-dr-lotbid">{money(lot.price)}</span>
        <span className="fsp-ui-grow fsp-sub">
          {standing}
          {lot.myAuto !== null ? ` · your auto-bid ${money(lot.myAuto)}` : ''}
        </span>
      </div>
      {seat && view.status !== 'complete' ? (
        <div className="fsp-dr-lotbtns">
          {!ahead ? (
            <>
              <Button
                size="xs"
                variant="primary"
                disabled={!can(one)}
                label={`Bid ${money(one)} on ${lot.player.name}`}
                onClick={() => actions.bid(lot.id, one)}
              >
                Bid {money(one)}
              </Button>
              {five <= lot.myMost ? (
                <Button
                  size="xs"
                  disabled={!can(five)}
                  label={`Bid ${money(five)} on ${lot.player.name}`}
                  onClick={() => actions.bid(lot.id, five)}
                >
                  Bid {money(five)}
                </Button>
              ) : null}
              <Button
                size="xs"
                variant="quiet"
                disabled={!can(one)}
                label={`Bid an amount on ${lot.player.name}`}
                onClick={() => onAsk({ kind: 'bid', lot })}
              >
                Bid…
              </Button>
            </>
          ) : null}
          <Button
            size="xs"
            variant="quiet"
            disabled={!usable || !live || (!ahead && !can(one))}
            label={`Auto-bid on ${lot.player.name}`}
            onClick={() => onAsk({ kind: 'auto', lot })}
          >
            {lot.myAuto !== null ? `Auto-bid ${money(lot.myAuto)}` : 'Auto-bid…'}
          </Button>
        </div>
      ) : null}
      {why !== null ? (
        <Text tone="secondary" size="xs" block>
          {why}
        </Text>
      ) : null}
    </div>
  );
}

/** What a typed amount is for: a bid, an auto-bid, or a nomination's opening bid. */
type Ask =
  | { kind: 'bid'; lot: DraftAuctionLot }
  | { kind: 'auto'; lot: DraftAuctionLot }
  | { kind: 'nominate'; playerId: string; forTeam: string | null };

/** The amount to bid, to bid up to, or to open at: one number, said what it does first. */
function AmountDialog(props: {
  view: DraftView;
  ask: Ask | null;
  actions: AuctionActions;
  onClose: () => void;
  detail: DetailLevel;
}) {
  const { ask, view } = props;
  if (ask === null || view.auction === null) return null;
  // KEYED, SO EACH OPENING STARTS FROM ITS OWN NUMBER.
  const key = ask.kind === 'nominate' ? `n:${ask.playerId}` : `${ask.kind}:${String(ask.lot.id)}`;
  return <AmountForm key={key} {...props} ask={ask} auction={view.auction} />;
}

function AmountForm({
  view,
  ask,
  auction,
  actions,
  onClose,
  detail,
}: {
  view: DraftView;
  ask: Ask;
  auction: DraftAuction;
  actions: AuctionActions;
  onClose: () => void;
  detail: DetailLevel;
}) {
  // THE LOWEST AMOUNT THAT DOES ANYTHING, to start from.
  const [amount, setAmount] = useState<number | null>(() =>
    ask.kind === 'nominate'
      ? auction.rules.minBid
      : ask.kind === 'auto'
        ? (ask.lot.myAuto ?? Math.min(ask.lot.myMost, ask.lot.price + 5))
        : ask.lot.price + 1,
  );
  const nominee =
    ask.kind === 'nominate'
      ? (view.available.find((r) => r.player.id === ask.playerId) ?? null)
      : null;
  const name = ask.kind === 'nominate' ? (nominee?.player.name ?? 'him') : ask.lot.player.name;
  const forTeam =
    ask.kind === 'nominate' && ask.forTeam !== null
      ? (view.seats.find((s) => s.team.id === ask.forTeam)?.team.name ?? null)
      : null;
  const most =
    ask.kind === 'nominate'
      ? ((ask.forTeam === null
          ? myMoney(view)?.most
          : auction.budgets.find((b) => b.teamId === ask.forTeam)?.most) ?? auction.rules.minBid)
      : ask.lot.myMost;
  const least =
    ask.kind === 'nominate'
      ? auction.rules.minBid
      : ask.kind === 'auto' && ask.lot.leaderTeamId === view.myTeamId
        ? ask.lot.price
        : ask.lot.price + 1;
  const value = ask.kind === 'nominate' ? (nominee?.value ?? null) : ask.lot.value;
  const ok = amount !== null && amount >= least && amount <= most;
  const title =
    ask.kind === 'nominate'
      ? `Put up ${name}`
      : ask.kind === 'auto'
        ? `Auto-bid on ${name}`
        : `Bid on ${name}`;
  const words =
    ask.kind === 'nominate'
      ? `The opening bid is ${forTeam === null ? 'yours' : `${forTeam}’s`}: if nobody bids more, he is ${forTeam === null ? 'yours' : 'theirs'} at it.`
      : ask.kind === 'auto'
        ? 'The most you will pay. The room bids for you, $1 over anybody else, up to it, and nobody else sees it.'
        : `The bid is ${money(ask.lot.price)}.`;
  const go = () => {
    if (!ok) return;
    onClose();
    if (ask.kind === 'nominate') actions.nominate(ask.playerId, amount, ask.forTeam);
    else if (ask.kind === 'auto') actions.autoBid(ask.lot.id, amount);
    else actions.bid(ask.lot.id, amount);
  };
  return (
    <Dialog opened onClose={onClose} title={title}>
      <Stack gap={3}>
        <Text block>{words}</Text>
        <NumberField
          label={ask.kind === 'nominate' ? 'Opening bid' : ask.kind === 'auto' ? 'Up to' : 'Bid'}
          prefix="$"
          value={amount}
          min={least}
          max={most}
          onChange={setAmount}
          description={`From ${money(least)} to ${money(most)}${detail !== 'simple' && value !== null ? ` · value ${money(value)}` : ''}`}
        />
        <Line gap={2} wrap>
          <Button variant="primary" disabled={!ok} onClick={go}>
            {amount === null
              ? 'Bid'
              : ask.kind === 'nominate'
                ? `Put him up at ${money(amount)}`
                : ask.kind === 'auto'
                  ? `Auto-bid to ${money(amount)}`
                  : `Bid ${money(amount)}`}
          </Button>
          {ask.kind === 'auto' && ask.lot.myAuto !== null ? (
            <Button
              variant="quiet"
              onClick={() => {
                onClose();
                actions.autoBid(ask.lot.id, null);
              }}
            >
              Take it off
            </Button>
          ) : null}
          <Button variant="quiet" onClick={onClose}>
            Not yet
          </Button>
        </Line>
      </Stack>
    </Dialog>
  );
}

/**
 * The block under the bar: every player up, with my bids on each, and the
 * last sale said for a moment (AUCTION.md §3.5).
 */
export function AuctionLots({
  view,
  now,
  actions,
  usable,
  stopped,
  detail,
  nominate,
  onNominated,
}: {
  view: DraftView;
  now: number;
  actions: AuctionActions;
  usable: boolean;
  /** The league's clock is stopped overnight: nothing sells now. */
  stopped: boolean;
  detail: DetailLevel;
  /** A player chosen to put up, waiting on his opening bid. */
  nominate: { playerId: string; forTeam: string | null } | null;
  onNominated: () => void;
}) {
  const auction = view.auction;
  const [ask, setAsk] = useState<Ask | null>(null);
  const seats = new Map(view.seats.map((s) => [s.team.id, s]));
  const nameOf = (teamId: string) =>
    teamId === view.myTeamId ? 'you' : (seats.get(teamId)?.team.ownerName ?? 'A team');

  // SOLD, SAID FOR A MOMENT where the player was (AUCTION.md §3.5): when the
  // board gains a player, never when an undo takes one back off it.
  const last = view.lastPick;
  const count = view.board.filter((c) => c.player !== null).length;
  const seen = useRef(count);
  const [sold, setSold] = useState<string | null>(null);
  useEffect(() => {
    const grew = count > seen.current;
    seen.current = count;
    if (!grew || last?.player == null || last.price === null) return;
    const to = last.teamId === view.myTeamId ? 'you' : nameOf(last.teamId);
    setSold(`Sold! ${last.player.name} to ${to} for ${money(last.price)}.`);
    const t = setTimeout(() => setSold(null), 4_000);
    return () => clearTimeout(t);
    // The sale is the trigger: one more player on the board, and nothing else.
  }, [count]);

  const asking: Ask | null = nominate !== null ? { kind: 'nominate', ...nominate } : ask;
  const close = () => {
    setAsk(null);
    if (nominate !== null) onNominated();
  };

  if (auction === null) return null;
  const parts: ReactNode[] = [];
  if (sold !== null)
    parts.push(
      <Callout key="sold" tone="good">
        {sold}
      </Callout>,
    );
  for (const lot of auction.lots) {
    parts.push(
      <LotCard
        key={lot.id}
        view={view}
        lot={lot}
        now={now}
        actions={actions}
        usable={usable}
        stopped={stopped}
        detail={detail}
        nameOf={nameOf}
        onAsk={setAsk}
      />,
    );
  }
  return (
    <>
      {parts.length > 0 ? <div className="fsp-dr-lots">{parts}</div> : null}
      <AmountDialog view={view} ask={asking} actions={actions} onClose={close} detail={detail} />
    </>
  );
}

/** The bar's words in an auction: who is nominating, or what is up, and my money. */
export function auctionBarText(
  view: DraftView,
  now: number,
): { caption: string; headline: string; sub: string; clock: string | null; urgent: boolean } {
  const a = view.auction!;
  const seats = new Map(view.seats.map((s) => [s.team.id, s]));
  const nom = a.nominating;
  const nomName = nom === null ? '' : (seats.get(nom.teamId)?.team.ownerName ?? 'A team');
  const paused = view.status === 'paused';
  const caption = paused
    ? 'Auction · paused'
    : nom?.isMine
      ? 'Auction · your nomination'
      : 'Auction';
  const up = a.lots.length;
  const headline = paused
    ? 'Paused'
    : up > 0 && a.rules.lotsAtOnce === 1
      ? 'Up for bids'
      : up > 0
        ? `${String(up)} up for bids${nom === null ? '' : nom.isMine ? ' · your nomination' : ` · ${nomName} nominating`}`
        : nom === null
          ? 'Bidding'
          : nom.isMine
            ? 'Your nomination'
            : `${nomName} is nominating`;
  const mine = moneyLine(view);
  const sub = [
    up > 0 && a.rules.lotsAtOnce === 1
      ? `Put up by ${a.lots[0]!.nominatedBy === view.myTeamId ? 'you' : (seats.get(a.lots[0]!.nominatedBy)?.team.ownerName ?? 'a team')}`
      : nom?.isMine
        ? 'Choose a player in the list'
        : null,
    mine,
  ]
    .filter(Boolean)
    .join(' · ');
  // THE NOMINATION'S CLOCK, while somebody is nominating.
  const left = nom?.deadlineAt ? Date.parse(nom.deadlineAt) - now : null;
  return {
    caption,
    headline,
    sub,
    clock: left === null || paused ? null : auctionClock(left),
    urgent: left !== null && left <= 10_000 && !isSlow(a),
  };
}

/** Every team's money: what it has left, the most it can bid, and its spots (AUCTION.md §3.2). */
export function MoneyPane({ view }: { view: DraftView }) {
  const a = view.auction;
  if (a === null) return null;
  const seats = new Map(view.seats.map((s) => [s.team.id, s]));
  return (
    <div className="fsp-dr-money">
      <Caption>Every team’s money</Caption>
      <table className="fsp-dr-table" aria-label="Every team’s money">
        <thead>
          <tr>
            <th scope="col">Team</th>
            {/* Each head says what it means when asked (B-374). */}
            <th scope="col" className="fsp-dr-num">
              <Term tip="Money left, with what the team is ahead on held back.">Left</Term>
            </th>
            <th scope="col" className="fsp-dr-num">
              <Term tip="The most it can bid, keeping the lowest bid for every other spot.">
                Most
              </Term>
            </th>
            <th scope="col" className="fsp-dr-num">
              <Term tip="Spots left to fill.">Spots</Term>
            </th>
          </tr>
        </thead>
        <tbody>
          {a.budgets.map((b) => {
            const s = seats.get(b.teamId);
            return (
              <tr key={b.teamId} data-mine={b.teamId === view.myTeamId || undefined}>
                <td>{b.teamId === view.myTeamId ? 'You' : (s?.team.ownerName ?? b.teamId)}</td>
                <td className="fsp-dr-num">{money(b.left)}</td>
                <td className="fsp-dr-num fsp-dr-strong">
                  {b.spotsLeft === 0 ? '—' : money(b.most)}
                </td>
                <td className="fsp-dr-num">{b.spotsLeft}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Text tone="secondary" size="xs" block>
        The most a team can bid keeps {money(a.rules.minBid)} for each of its other empty spots.
      </Text>
    </div>
  );
}
